import type {
  TypeButton,
  TypeDisplayTyping,
  TypeImagePosition,
  TypeLinkTarget,
  TypeMessage,
  TypeMessageForm,
  TypeMessageLayout,
  TypeMessageSender,
  TypeMessageStructure,
  TypePostBackAction,
  TypePostBackType,
  TypeStatusAPI,
  TypeTagOrientation,
  TypeTagPosition,
} from "../enums";
import type { MessageStructureComponent } from "./message_structure_component";

export interface IFormData<T> {
  label: string;
  key: T;
}

export interface IComponentStyle {
  // name?: string
  type?: string;
  position?: TypeTagPosition | TypeImagePosition;
  imageUrlPlaceholder?: string;
  imageAltPlaceholder?: string;
  height?: string;
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  maxLines?: number;
  fontWeight?: string;
  fontSize?: string;
  textAlign?: string;
  tagColor?: string;
  content?: string;
  color?: string;
  backgroundColor?: string;
  background?: string;
  borderRadius?: string;
  margin?: string;
  padding?: string;
  objectFit?: string;
  aspectRatio?: string;
  isVisible?: boolean;
  tagOrientation?: TypeTagOrientation;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
}

export interface IComponent {
  listItem?: IElementListItem;
  headerImage?: IElementImage;
  line1?: IElementText;
  line2?: IElementText;
  html?: IElementText;
  // Keep existing button properties for backward compatibility
  button1?: IElementButton;
  button2?: IElementButton;
  button3?: IElementButton;
  tag?: IElementText;
  enableShowMore?: boolean;
  // Add new dynamic buttons structure
  buttons?: IDynamicButtonMap;
}

export interface IStyleComponentItem {
  carouselContainer?: IComponentStyle;
  carouselItem?: IComponentStyle;
  headerImage?: IComponentStyle;
  line1?: IComponentStyle;
  line2?: IComponentStyle;
  // Keep existing button styles for backward compatibility
  button1?: IComponentStyle;
  button2?: IComponentStyle;
  button3?: IComponentStyle;
  tag?: IComponentStyle;
  html?: IComponentStyle;
  ellipsis?: IComponentStyle;
  // Add support for dynamic button styles
  buttons?: Record<string, IComponentStyle>;
}

export interface ISettingsStructure {
  carouselSettings?: {
    containerSettings: {
      componentItems: IStyleComponentItem;
    };
  };
}

export interface IClickAction {
  type: string;
  postback: string;
  postbackAction: string;
  replyText: string;
  closeModal: boolean;
  settings: {
    showReplyText: boolean;
  };
  metadata: IModelMetadata;
}

export interface IElementButton {
  label: string;
  style: {
    type: TypeButton;
  };
  clickAction: IClickAction;
}

export interface IElementListItem {
  clickAction: IClickAction;
}

export interface IElementText {
  content: string;
}

export interface IElementImage {
  type?: string;
  imageUrl?: string;
  alt?: string;
}

export interface IResponseAWSConfig {
  awsServerURL: string;
  awsThemeFilePath: string;
  awsImageLogoPath: string;
  awsPopupConfigPath: string;
}

export interface IModelLegalEntries {
  displayText: string;
  url: string;
}

export interface IPayloadPageConfig {
  ChatPageId: string;
  ConvId: string;
  ExtMsgId: string;
  PageReferralUrl: string;
  ParentSiteReferralUrl: string;
  ParentSiteUrl: string;
  PopupId: string;
  Url: string;
  iFramed: boolean;
  ip: string;
  userAgent: string;
}

export interface IResponseIP {
  ip: string;
}

export interface IResponsePageConfig {
  companyToken?: string | null;
  extBotId: number;
  sendMessageUrl: string;
  maintainState: boolean;
  avatarBotImage: string;
  avatarHumanImage: string;
  headerImage: string;
  backgroundImage: string;
  headerImageDestinationURL: string;
  showCameraBtn: boolean;
  showInfoBtn: boolean;
  hidePageTitle: boolean;
  hideInputContainer: boolean;
  inputPlaceHolder: string;
  hideTagLine: boolean;
  enableAdminMode: boolean;
  enableTermsConsent: boolean;
  termsConsentPayload: string;
  termsConsentExpiration: string;
  pageName: string;
  legalEntries: IModelLegalEntries[];
  companyId: number;
  themeUrl: string;
  headerThemeUrl: string;
  pageTitle: string;
  welcomeMessage: IModelMessageStructure;
  errorMessage: string;
  pageURL: string;
  webChatConnectionRetryWarningCount: string;
  webChatConnectionRetryInterval: string;
  typingMessageSecondsInterval: string;
  clientVersion: string;
  minimumClientVersion: string;
  webApiAddress: string;
  attemptNoofInvalidCode: number;
  attemptNoofResendCode: number;
  dashboardAddress: string;
  mfaExpiration: number;
  mfaEnabled: boolean;
  mfaEnableDateTime: string;
  convId: string;
  newMessageProcessing: boolean;
  enableMessageNavigation: boolean;
}

interface IModelFont {
  size?: string;
  bold?: boolean;
  italic?: boolean;
  color?: string;
  alignment?: string;
}

interface IModelFormMessage {
  type: TypeMessageForm;
  label?: string;
  font?: IModelFont;
  name?: string;
  layout?: TypeMessageLayout;
  value?: string | number;
  imageUrl?: string;
  alt?: string;
  maxValue?: number;
  minValue?: number;
  isRequired?: boolean;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  clickAction?: IModelClickAction;
  style?: IModelMessageStyle;
}

interface IModelClickAction {
  type: TypePostBackType;
  postback: string;
  postbackAction?: TypePostBackAction;
  replyText?: string;
  property?: string;
  propertyValue?: string;
  metadata: [];
  settings?: IModelMessageSettings;
}

interface IModelMetadata {
  SocketId?: string;
  UserAgent?: string;
  BrowserUuid?: string;
  IP?: string;
  PageReferralUrl?: string;
  IFramed?: boolean;
  PopupId?: number;
  ChatPageId?: number;
  UserLat?: string;
  UserLng?: string;
  LocationBlocked?: boolean;
  ParentSiteUrl?: string;
  ParentSiteReferralUrl?: string;
  Url?: string;
  cf?: string;
  AdminMode?: number;
  TermsOfServiceAccepted?: number;
}

export interface IModelMessageSettings extends ISettingsStructure {
  showReplyText?: boolean;
  isEnterAction?: boolean;
  isCancelAction?: boolean;
  isHidden?: boolean;
  messageId?: string;
  sendFormEntries?: boolean;
  disableHideOnSend?: boolean;
  hideInputContainer?: boolean;
  inputHidden?: boolean;
  hideInputContainerAlways?: boolean;
  hideQuickRepliesOnSend?: boolean;
  showOneQuickReplyPerLine?: boolean;
  clearScreen?: boolean;
  iframeBackButtonLabel?: string;
  forceMaxWidth?: boolean;
  browserTarget?: TypeLinkTarget;
  browserTargetDesktop?: TypeLinkTarget;
  browserTargetMobile?: TypeLinkTarget;
  hideInChatBackButton?: boolean;
  passUserLocation?: boolean;
  hideButtonsOnSend?: boolean;
}

interface IModelMessageStyle {
  type?: "secondary" | "primary";
  size?: string;
  width?: string | number;
  height?: string | number;
}

interface IModelMessageComponent extends IComponent {
  type?: TypeMessage;
  content?: string;
  clickAction?: IModelClickAction;
  style?: IModelMessageStyle | null;
  label?: string;
  metadata?: IModelMetadata;
  settings?: IModelMessageSettings;
  imageUrl?: string;
  alt?: string;
  name?: string;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  templateName?: string;
  formHeader?: IModelFormMessage[];
  formItems?: IModelFormMessage[];
  formFooter?: IModelFormMessage[];
}

export interface IModelMessageStructure {
  type: TypeMessageStructure;
  components: IModelMessageComponent[];
  showModal?: boolean;
  settings?: IModelMessageSettings;
}

interface IModelConversation {
  extConvId: string;
  extUserId: string;
  extUserIdSourceTypeId: number;
}

interface IModelChatMetadata {
  inputText?: string;
  responseId?: string;
}

interface IModelChatTypingIndicator {
  displayType: TypeDisplayTyping;
  displayValue: string;
  timeout: number;
}

interface IModelChatProcessingIndicator {
  displayType: TypeDisplayTyping;
  displayIcon: string;
  displayText: string;
  timeout: number;
}

interface IModelChatRecordInfo {
  senderType: TypeMessageSender;
  messageCacheId: string;
}

export interface IModelChatMessage {
  type?: TypeMessageStructure;
  layout?: TypeMessageLayout;
  keys?: IModelConversation;
  structures?: IModelMessageStructure[];
  quickReplies?: IModelMessageComponent[];
  metadata?: IModelChatMetadata;
  status?: TypeStatusAPI;
  settings?: IModelMessageSettings | IModelMessageSettings[];
  typingIndicator?: IModelChatTypingIndicator;
  processingIndicator?: IModelChatProcessingIndicator;
  recordInfo?: IModelChatRecordInfo;
  messageTime?: string;
  metaData?: IModelMetadata;
  version?: string;
}

export interface IModelStoreMessage {
  id?: string;
  type: TypeMessageSender;
  status?: string;
  contents?: IModelChatMessage;
  newId?: string;
  buttonMessageStructure?: IModelMessageStructure;
}

export interface IModelConsentOption {
  msg: string;
  shouldSend?: boolean;
  hideInChat?: boolean;
  messageStructure?: IModelMessageComponent;
}

export interface IModelFormInput {
  fieldId?: string;
  fieldName?: string;
  fieldValue?: string;
}

export interface IModelVue3SlideEnd {
  currentSlideIndex: number;
  slidesCount: number;
  prevSlideIndex: number;
}

//#region Dynamic Button Configuration

/**
 * Configuration object for dynamic button operations.
 * Provides dependency injection for component-specific data.
 * @property component - The component instance containing button data.
 * @property componentStyle - Component style configuration for dynamic buttons.
 */
export interface IDynamicButtonConfig {
  component: IComponent;
  componentStyle: IStyleComponentItem;
}

//#endregion

//#region Dynamic Button Items

/**
 * SD-8668: Represents a standardized dynamic button item for rendering in UI components.
 * @property key - Unique identifier for the button item.
 * @property component - The button's component data structure.
 * @property style - The style object to be applied to the button.
 */
export interface IDynamicButtonItem {
  key: string;
  component?: MessageStructureComponent;
  style?: any;
}

/**
 * Represents a dynamic mapping of button identifiers to button elements.
 *
 * This interface extends the Record type, where:
 * - The keys are strings, typically representing unique button identifiers
 * - The values are IElementButton objects, representing the button elements
 */
export interface IDynamicButtonMap extends Record<string, IElementButton> {}

//#endregion
